/**
 * Business-line picker for store creation.
 *
 * A boutique rarely sells one thing. "Clothes AND shoes AND home textiles" could
 * not be expressed before: the store `category` was a single enum. This panel
 * lets a merchant declare the lines they actually sell, which is what drives the
 * product types (and therefore the size/pointure/colour fields) they get later.
 *
 * The backend remains the source of truth for what each line means — this mirror
 * exists only so the wizard can render without waiting on a request.
 */

import React, { useMemo } from "react";
import { Shirt, Footprints, ShoppingBag, Home, Sofa, Package } from "lucide-react";
import { BUSINESS_LINES } from "../../api/taxonomy";

const ICONS: Record<string, typeof Shirt> = {
  shirt: Shirt,
  footprints: Footprints,
  "shopping-bag": ShoppingBag,
  home: Home,
  sofa: Sofa,
  package: Package,
};

const LINE_ICON: Record<string, string> = {
  FASHION_CLOTHING: "shirt",
  FASHION_SHOES: "footprints",
  FASHION_BAGS: "shopping-bag",
  HOME_TEXTILE: "home",
  HOME_FURNITURE: "sofa",
  GENERAL_RETAIL: "package",
};

interface BusinessLinePickerProps {
  value: string[];
  onChange: (lines: string[]) => void;
  disabled?: boolean;
}

export default function BusinessLinePicker({
  value,
  onChange,
  disabled = false,
}: BusinessLinePickerProps) {
  const selected = useMemo(() => new Set(value), [value]);
  const toggle = (key: string) => {
    // Build a plain array (no Set iteration) — `target` here is es5.
    const next = selected.has(key) ? value.filter((item) => item !== key) : [...value, key];
    onChange(next);
  };

  return (
    <div className="grid gap-2 sm:grid-cols-2">
      {BUSINESS_LINES.map((line) => {
        const isSelected = selected.has(line.key);
        const Icon = ICONS[LINE_ICON[line.key] || "package"] || Package;

        return (
          <button
            key={line.key}
            type="button"
            disabled={disabled}
            aria-pressed={isSelected}
            onClick={() => toggle(line.key)}
            className={`flex items-start gap-3 rounded-xl border p-3 text-left transition disabled:cursor-not-allowed disabled:opacity-60 ${
              isSelected
                ? "border-cyan-400 bg-cyan-50/70 ring-2 ring-cyan-200"
                : "border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50"
            }`}
          >
            <span
              className={`mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${
                isSelected ? "bg-cyan-500 text-white" : "bg-slate-100 text-slate-500"
              }`}
            >
              <Icon size={18} />
            </span>

            <span className="min-w-0 flex-1">
              <span className="block text-sm font-semibold text-slate-900">{line.label}</span>
              <span className="mt-0.5 block text-xs text-slate-500">{line.description}</span>
            </span>

            <span
              className={`mt-1 h-4 w-4 shrink-0 rounded-full border-2 ${
                isSelected ? "border-cyan-500 bg-cyan-500" : "border-slate-300"
              }`}
            >
              {isSelected && <span className="block h-full w-full rounded-full bg-white scale-[0.35]" />}
            </span>
          </button>
        );
      })}
    </div>
  );
}